import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { apiFetch, SESSION_EXPIRED_EVENT, type ApiError } from '../lib/api.js';

export type ChatRole = 'viewer' | 'vip' | 'mod' | 'broadcaster';

export interface AuthUser {
  uid: string;
  twitchId: string;
  login: string;
  displayName: string;
  avatar: string | null;
  role: ChatRole;
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  isLoggedIn: boolean;
  isMod: boolean;
  isBroadcaster: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await apiFetch<AuthUser>('/api/auth/me');
      setUser(me);
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr?.status === 401) {
        setUser(null);
      } else {
        console.error('AuthContext: /me failed', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch('/api/auth/logout', { method: 'POST' });
    } catch (err) {
      console.error('AuthContext: logout failed', err);
    }
    setUser(null);
  }, []);

  useEffect(() => {
    // If a ?key= param is present (Meld Studio dock / embed), exchange it for
    // a session cookie before the regular /me check — this auto-logs in the
    // key owner (usually broadcaster) without a manual Twitch OAuth flow.
    const keyParam = new URLSearchParams(window.location.search).get('key');
    if (keyParam) {
      apiFetch<{ ok: boolean }>(`/api/auth/key-session?key=${encodeURIComponent(keyParam)}`)
        .then(() => refresh())
        .catch(() => refresh());
    } else {
      void refresh();
    }
  }, [refresh]);

  useEffect(() => {
    function onSessionExpired(): void {
      setUser(null);
    }
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      isLoggedIn: !!user,
      isMod: user?.role === 'mod' || user?.role === 'broadcaster',
      isBroadcaster: user?.role === 'broadcaster',
      refresh,
      logout,
    }),
    [user, loading, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
